import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ScrollableSection from '@travel/traveler-core/components/ScrollableSection';
import useDeviceType, { TABLET_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { isEmptyArray, isNotEmptyArray } from '@travel/utils';

import ContentsCarousel from 'components/ContentsCarousel';
import WithSkeletonLoading from 'components/WithSkeletonLoading';
import {
  SUGGESTED_FEATURE_COUNT,
  SUGGESTED_MEDIA_COUNT,
} from 'pages/ProviderInformationPage/constants';
import { getRequestBody } from 'pages/ProviderListPage/resolver';

import { getItem as getProviderItem } from 'store/providerInformation/selectors';
import { setRenderSimilarProviders } from 'store/providerRatePlan/actions';
import { fetchSuggestedProviderList } from 'store/suggestedProviderList/actions';
import {
  getIsFetchingSuggestedProviders,
  getSuggestedProviders,
} from 'store/suggestedProviderList/selectors';

import { Translate } from 'core/translate';
import { ProviderItem, SearchFormQuery } from 'ProviderList-Types';

import ProviderBox from './components/ProviderBox';

import styles from './similarProviders.module.scss';

const PC_IMAGE_WIDTH = 286;

type Props = {
  query: SearchFormQuery;
  className: string;
  pageIdentifier: string;
};

function SimilarProviders(props: Props) {
  const { query, pageIdentifier } = props;

  const deviceType = useDeviceType();
  const isTablet = deviceType === TABLET_SCREEN;
  const dispatch = useDispatch();
  const { providers, breadcrumb } = useSelector(getSuggestedProviders);
  const { id, pathId } = useSelector(getProviderItem);
  const isFetchingList = useSelector(getIsFetchingSuggestedProviders);
  const similarProviders = isNotEmptyArray(providers) ? providers.filter(p => p.id !== id) : [];
  const isEmptyList = isEmptyArray(similarProviders);
  const areaName = isNotEmptyArray(breadcrumb) && breadcrumb?.[1] ? breadcrumb[1]?.name : '';

  const fetchSimilarProvider = useCallback(
    (pathId, pageIdentifier, query) => {
      const requestBody = getRequestBody({
        query: { ...query },
        params: { pageIdentifier: pageIdentifier },
      });

      dispatch(
        fetchSuggestedProviderList(
          {
            ...requestBody,
            mediaCounts: SUGGESTED_MEDIA_COUNT,
            featureCounts: SUGGESTED_FEATURE_COUNT,
            pathId: pathId,
            limit: 20,
            offset: 0,
          },
          { isSuggestionSearch: true },
        ),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    fetchSimilarProvider(pathId, pageIdentifier, query);
    return () => {
      dispatch(setRenderSimilarProviders(false));
    };
  }, [dispatch, fetchSimilarProvider, pageIdentifier, pathId, query]);

  if (isEmptyList) {
    return null;
  }

  const innerContent = similarProviders?.map((provider: ProviderItem) => (
    <ProviderBox
      key={provider.id}
      item={provider}
      onPage={'providerListDated'}
      areaName={areaName}
      query={props.query}
    />
  ));

  return (
    <div className={props.className} data-testid="similarProviderList-wrapper">
      <section className={styles.container}>
        <h2 className={styles.title}>{<Translate id="Provider_Info.Menu.Similar_Hotels" />}</h2>
        <WithSkeletonLoading
          isLoading={isFetchingList}
          count={4}
          customStyle="image"
          direction="row"
          skeletonImageClassName={styles.skeletonItem}
        >
          {!isTablet ? (
            <ContentsCarousel
              fixedCardWidth={PC_IMAGE_WIDTH}
              className={styles.carousel}
              contentsContainerClassName={styles.contentsContainer}
              displayCount={4}
            >
              {innerContent}
            </ContentsCarousel>
          ) : (
            <ScrollableSection childClassName={styles.scrollableSectionItem}>
              {innerContent}
            </ScrollableSection>
          )}
        </WithSkeletonLoading>
      </section>
    </div>
  );
}

export default SimilarProviders;
